import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Group } from '@mantine/core';
import { useAuth } from '../../context/Auth/AuthContext';
import Navigation from '../Home/Navigation';
import VerticalNav from '../Home/VerticalNav';
import ThumbnailCanvas from './components/ThumbnailCanvas';
import ThumbnailRightPanel from './components/ThumbnailRightPanel';
import ResolutionSelector, { Resolution, commonResolutions } from './components/ResolutionSelector';
import { useThumbnailEditor } from './hooks/useThumbnailEditor';
import { useNotification } from '../../context/NotificationContext';
import { downloadImage } from '../../services/autoDownloadService';
import { getApiUrl } from '../../config/api';
import { StepIndicatorNames } from '../../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faDownload, 
  faSave,
  faUndo,
  faRedo,
  faPencilAlt
} from '@fortawesome/free-solid-svg-icons';
import styles from './ThumbnailEditor.module.css';
import ProcessingDashboard from '../ProcessingDashboard/ProcessingDashboard';

const ThumbnailEditor: React.FC = () => {
  const { t } = useTranslation('thumbnailEditor');
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const { user, isLoading: authLoading } = useAuth(); // Get user and loading state from auth context
  const [projectTitle, setProjectTitle] = useState('Untitled Project');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [generatedThumbnailUrl, setGeneratedThumbnailUrl] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 1280, height: 720 }); // Default fallback
  const [targetResolution, setTargetResolution] = useState<Resolution>(commonResolutions[0]); // Default to YouTube 16:9
  
  const {
    // Editor state
    baseImage,
    imageAdjustments,
    textElements,
    shapeElements,
    assetElements,
    selectedElement,
    canvasSettings,
    
    // Loading states
    isLoading,
    isGenerating,
    
    // Actions
    loadProject,
    updateImageAdjustments,
    addTextElement,
    updateTextElement,
    removeTextElement,
    addShapeElement,
    updateShapeElement,
    removeShapeElement,
    addAssetElement,
    updateAssetElement,
    removeAssetElement,
    selectElement,
    generateFinalThumbnail,
    generateFinalThumbnailCanvas, // Canvas method
    generateFinalThumbnailFFmpeg, // FFmpeg method
    saveThumbnailProgress,
    loadThumbnailProgress,
    
    // History
    undo,
    redo,
    canUndo,
    canRedo
  } = useThumbnailEditor();

  // Tab management state
  const [activeTab, setActiveTab] = useState<'image' | 'text' | 'shapes' | 'assets'>('image');
  const [project, setProject] = useState<any>(null);

  // Function to determine which tab to switch to based on selected element
  const getTabForElement = (elementId: string | null): 'image' | 'text' | 'shapes' | 'assets' => {
    if (!elementId) return activeTab; // Keep current tab if nothing selected
    
    if (elementId === 'base-image') return 'image';
    
    // Check if it's a text element
    if (textElements.find(el => el.id === elementId)) return 'text';
    
    // Check if it's a shape element
    if (shapeElements.find(el => el.id === elementId)) return 'shapes';
    
    // Check if it's an asset element
    if (assetElements.find(el => el.id === elementId)) return 'assets';
    
    return activeTab; // Fallback to current tab
  };

  // Enhanced select element function that also switches tabs
  const handleSelectElement = (elementId: string | null) => {
    selectElement(elementId);
    
    // Switch to appropriate tab when selecting an element
    if (elementId) {
      const targetTab = getTabForElement(elementId);
      setActiveTab(targetTab);
    }
  };

  // Load project data on mount
  useEffect(() => {
    if (projectId) {
      // Load project details to get title
      const loadProjectDetails = async () => {
        try {
          // Get authentication token
          const token = localStorage.getItem('authToken');
          const headers: Record<string, string> = {
            'Content-Type': 'application/json'
          };
          
          // Add authorization header if token exists
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await fetch(getApiUrl(`/projects/${projectId}`), {
            headers
          });
          if (response.ok) {
            const project = await response.json();
            setProject(project);
            setProjectTitle(project.title || 'Untitled Project');
          }
        } catch (error) {
          console.error('Failed to load project details:', error);
        }
      };
      
      loadProjectDetails();
      
      // Get base image URL from search params (from generation)
      const baseImageUrl = searchParams.get('baseImage') || undefined;
      loadProject(projectId, baseImageUrl);
      // Try to load any saved progress
      loadThumbnailProgress(projectId).catch(() => {
        // Ignore errors - just means no saved progress
      });
    }
  }, [projectId, searchParams, loadProject, loadThumbnailProgress]);

  const handleGoBack = () => {
    // Navigate based on URL parameter - same logic as VideoEditor
    if (searchParams.get('source') === 'video-editor') {
      navigate(`/caption?project-id=${projectId}`);
    } else {
      navigate('/thumbnail-landing');
    }
  };

  const handleUpdateTitle = async (newTitle: string) => {
    if (!projectId || !newTitle.trim()) return;
    
    try {
      // Get authentication token
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Call the projects API to update the title (same as VideoEditor)
      const response = await fetch(getApiUrl(`/projects/${projectId}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ title: newTitle }),
      });

      if (!response.ok) {
        throw new Error('Failed to update project title');
      }

      setProjectTitle(newTitle);
      setIsEditingTitle(false);
      // showNotification({
      //   title: 'Success',
      //   message: 'Project title updated successfully',
      //   type: 'success'
      // });
    } catch (error) {
      console.error('Error updating project title:', error);
      showNotification({
        title: t('thumbnailEditor.notifications.titleUpdateError'),
        message: t('thumbnailEditor.notifications.titleUpdateError'),
        type: 'error'
      });
    }
  };

  const handleSave = async () => {
    if (!projectId) return;
    
    try {
      // Call the correct function name
      await saveThumbnailProgress(projectId);
      // showNotification({
      //   title: 'Success',
      //   message: 'Thumbnail progress saved successfully!',
      //   type: 'success'
      // });
    } catch (error) {
      showNotification({
        title: t('thumbnailEditor.notifications.saveError'),
        message: t('thumbnailEditor.notifications.saveError'),
        type: 'error'
      });
    }
  };

  const handleCanvasDimensionsChange = useCallback((dimensions: { width: number; height: number }) => {
    setCanvasDimensions(prev => {
      // Only update if dimensions actually changed to prevent unnecessary re-renders
      if (prev.width !== dimensions.width || prev.height !== dimensions.height) {
        return dimensions;
      }
      return prev;
    });
  }, []);

  const handleGenerate = async () => {
    if (!projectId) return;
    
    setIsExporting(true);
    setExportProgress(0);
    
    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setExportProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Use the new FFmpeg-based method for high-quality processing
      const result = await generateFinalThumbnailFFmpeg(projectId, canvasDimensions, targetResolution);
      
      clearInterval(progressInterval);
      setExportProgress(100);
      setGeneratedThumbnailUrl(result.downloadUrl);
      
      // Auto-download the thumbnail immediately after generation
      try {
        const downloadUrl = result.downloadUrl;
        console.log('🔗 Generated thumbnail URL type:', downloadUrl?.startsWith('data:') ? 'Data URL' : 'HTTP URL');
        console.log('🔗 Generated thumbnail URL length:', downloadUrl?.length);
        
        // Validate URL before attempting download
        if (!downloadUrl) {
          throw new Error('No download URL provided');
        }
        
        const filename = `thumbnail-${projectTitle || projectId}-${Date.now()}.png`;
        console.log('📁 Download filename:', filename);
        
        // Handle data URLs differently from HTTP URLs
        if (downloadUrl.startsWith('data:')) {
          console.log('📥 Downloading data URL directly...');
          
          // Convert data URL to blob and download directly
          const response = await fetch(downloadUrl);
          const blob = await response.blob();
          
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
          
          console.log('✅ Data URL auto-download completed successfully');
        } else {
          console.log('📥 Downloading HTTP URL via service...');
          
          // Use downloadImage service for HTTP URLs (handles S3 CORS)
          await downloadImage(downloadUrl, {
            filename,
            setIsProcessing: undefined
          });
          
          console.log('✅ HTTP URL auto-download completed successfully');
        }
        
        // showNotification({
        //   title: 'Success',
        //   message: 'Thumbnail generated and downloaded automatically!',
        //   type: 'success'
        // });
        
      } catch (downloadError) {
        console.error('❌ Auto-download failed:', downloadError);
        console.error('❌ Download error details:', {
          message: downloadError.message,
          stack: downloadError.stack,
          url: result.downloadUrl
        });
        
        // Still show success for generation, but note download issue
        showNotification({
          title: t('thumbnailEditor.notifications.thumbnailGenerated'),
          message: t('thumbnailEditor.notifications.downloadFailed'),
          type: 'error'
        });
      }
      
      // Reset export state after a short delay
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
      }, 2000);
      
    } catch (error) {
      setIsExporting(false);
      setExportProgress(0);
      showNotification({
        title: t('thumbnailEditor.notifications.generateError'),
        message: error instanceof Error ? error.message : t('thumbnailEditor.notifications.generateError'),
        type: 'error'
      });
    }
  };

  const handleDownload = async () => {
    // Determine which URL to download - generated thumbnail or base image
    const urlToDownload = generatedThumbnailUrl || baseImage;
    
    if (!urlToDownload) return;
    
    try {
      console.log('🔗 Download URL type:', urlToDownload?.startsWith('data:') ? 'Data URL' : 'HTTP URL');
      
      const filename = `thumbnail-${projectTitle || projectId}-${Date.now()}.png`;
      
      // Handle data URLs differently from HTTP URLs
      if (urlToDownload.startsWith('data:')) {
        console.log('📥 Downloading data URL directly...');
        
        // Convert data URL to blob and download directly
        const response = await fetch(urlToDownload);
        const blob = await response.blob();
        
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        
        console.log('✅ Data URL download completed successfully');
      } else {
        console.log('📥 Downloading HTTP URL via service...');
        
        // Use downloadImage service for HTTP URLs (handles S3 CORS)
        await downloadImage(urlToDownload, {
          filename,
          setIsProcessing: undefined
        });
        
        console.log('✅ HTTP URL download completed successfully');
      }
      
      // showNotification({
      //   title: 'Success',
      //   message: 'Thumbnail downloaded successfully!',
      //   type: 'success'
      // });
      
    } catch (error) {
      console.error('❌ Download failed:', error);
      console.error('❌ Download error details:', {
        message: error.message,
        stack: error.stack,
        url: urlToDownload
      });
      
      showNotification({
        title: t('thumbnailEditor.notifications.downloadError'), 
        message: t('thumbnailEditor.notifications.downloadError'),
        type: 'error'
      });
    }
  };

  const handleNavSelected = (key: StepIndicatorNames) => {
    console.log("Nav selection changed to:", key);
    
    // Handle navigation based on selected step (same pattern as title editor)
    switch (key) {
      case StepIndicatorNames.IdeaCreation:
        // Navigate to prompt generation for this project
        if (projectId) {
          navigate(`/prompt-generator/${projectId}`);
        } else {
          navigate('/prompt-generator');
        }
        break;
        
      case StepIndicatorNames.ScriptGeneration:
        // Navigate to script generation for this project
        if (projectId) {
          navigate(`/script-generator/${projectId}`);
        } else {
          navigate('/script-generator');
        }
        break;
        
      case StepIndicatorNames.AudioGeneration:
        // Navigate to standalone audio generation
        navigate('/audio-generation');
        break;
        
      case StepIndicatorNames.SoundGeneration:
        // Navigate to sound generation for this project
        if (projectId) {
          navigate(`/sound-editor/${projectId}`);
        } else {
          navigate('/sound-generation');
        }
        break;
        
      case StepIndicatorNames.ContentCreation:
        if (projectId) {
          // Navigate to content creation with the existing project ID
          navigate(`/create-content?project-id=${projectId}&source=thumbnail-editor`);
        }
        break;
        
      case StepIndicatorNames.CaptionCreation:
        if (projectId) {
          navigate(`/caption?project-id=${projectId}&source=thumbnail-editor`);
        } else {
          navigate('/caption');
        }
        break;
        
      case StepIndicatorNames.TitleDescTag:
        if (projectId) {
          navigate(`/title-description/${projectId}`);
        }
        break;
        
      // Add other navigation cases as needed
      default:
        break;
    }
  };
  if (isLoading || authLoading) {
    return (
      <div className={styles.container}>
        <Navigation />
        <VerticalNav 
          projectData={project}
          handleNavSelected={handleNavSelected} 
          initialStep={StepIndicatorNames.ThumbnailCreation} 
        />
        <main className={styles.main}>
          <div className={styles.loadingContainer}>
            <Loader size="lg" />
            <p>{t('thumbnailEditor.loading')}</p>
          </div>
        </main>
      </div>
    );
  }

  if (isGenerating || isExporting) {
    return (
      <div className={styles.container}>
        <Navigation />
        <VerticalNav 
          projectData={project}
          handleNavSelected={handleNavSelected} 
          initialStep={StepIndicatorNames.ThumbnailCreation} 
        />
        <ProcessingDashboard 
          status={isExporting ? "processing" : "generating"}
          progress={isExporting ? exportProgress : 75}
          message={isExporting ? t('thumbnailEditor.processing.composingThumbnail') : t('thumbnailEditor.processing.generatingThumbnail')}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Navigation />
      <VerticalNav 
        projectData={project}
        handleNavSelected={handleNavSelected} 
        initialStep={StepIndicatorNames.ThumbnailCreation} 
      />
      
      <main className={styles.main}>
        <Group style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Left section - Back button, title, project title */}
          <section>
            <div style={{ display: "flex", justifyContent: 'space-between', alignItems: "center", width: "100%" }}>
              <div style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "-0.50rem" }}>
                  <Button 
                    className={styles.backButton}
                    onClick={handleGoBack}
                    title={t('thumbnailEditor.buttons.back')}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </Button>
                  
                  <h1 className={styles.pageTitle}>
                    {t('thumbnailEditor.title')}
                  </h1>
                </div>
                
                {/* Project Title moved under page title */}
                <div className={styles.titleContainer} style={{ marginLeft: "4rem" }}>
                  {isEditingTitle ? (
                    <input
                      type="text"
                      value={projectTitle}
                      onChange={(e) => setProjectTitle(e.target.value)}
                      onBlur={() => handleUpdateTitle(projectTitle)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateTitle(projectTitle);
                        if (e.key === 'Escape') {
                          setIsEditingTitle(false);
                          // Reset to original title if needed
                        }
                      }}
                      className={styles.projectTitleInput}
                      autoFocus
                    />
                  ) : (
                    <>
                      <h2 className={styles.projectTitle}>{projectTitle}</h2>
                      <button 
                        className={styles.editButton}
                        onClick={() => setIsEditingTitle(true)}
                        title={t('thumbnailEditor.buttons.editTitle')}
                      >
                        <FontAwesomeIcon style={{fontSize: "14px"}} icon={faPencilAlt} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Right section - Action buttons */}
          <section>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <Button
                className={styles.historyButton}
                onClick={undo}
                disabled={!canUndo}
                title={t('thumbnailEditor.buttons.undo')}
              >
                <FontAwesomeIcon icon={faUndo} />
              </Button>
              <Button
                className={styles.historyButton}
                onClick={redo}
                disabled={!canRedo}
                title={t('thumbnailEditor.buttons.redo')}
              >
                <FontAwesomeIcon icon={faRedo} />
              </Button>
              <Button
                className={styles.saveButton}
                onClick={handleSave}
                title={t('thumbnailEditor.buttons.saveProgress')}
              >
                <FontAwesomeIcon icon={faSave} />
                {t('thumbnailEditor.buttons.save')}
              </Button>
              <Button
                className={styles.generateButton}
                onClick={handleGenerate}
                disabled={isExporting || !baseImage}
                title={t('thumbnailEditor.buttons.composeFinal')}
              >
                <FontAwesomeIcon icon={faDownload} />
                {isExporting ? t('thumbnailEditor.buttons.generating') : t('thumbnailEditor.buttons.generate')}
              </Button>
              {/* Show download button if there's a thumbnail with S3 URL or if user has generated one */}
              {((baseImage && baseImage.includes('aiapptest2.s3.amazonaws.com')) || generatedThumbnailUrl) && (
                <Button
                  className={styles.downloadButton}
                  onClick={handleDownload}
                  title={t('thumbnailEditor.buttons.downloadThumbnail')}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </Button>
              )}
            </div>
          </section>
        </Group>

        {/* Main content area */}
        <div className={styles.contentGrid}>
          {/* Center - Canvas with Resolution Selector */}
          <div className={styles.center}>
            <ResolutionSelector
              currentResolution={targetResolution}
              onResolutionChange={setTargetResolution}
            />
            <ThumbnailCanvas
              baseImage={baseImage}
              imageAdjustments={imageAdjustments}
              textElements={textElements}
              shapeElements={shapeElements}
              assetElements={assetElements}
              selectedElement={selectedElement}
              onSelectElement={handleSelectElement}
              onUpdateTextElement={updateTextElement}
              onUpdateShapeElement={updateShapeElement}
              onUpdateAssetElement={updateAssetElement}
              onUpdateImageAdjustments={updateImageAdjustments}
              onCanvasDimensionsChange={handleCanvasDimensionsChange}
              canvasSettings={canvasSettings}
              targetResolution={targetResolution}
            />
          </div>

          {/* Right - Control Panel */}
          <div className={styles.right}>
            <ThumbnailRightPanel
              imageAdjustments={imageAdjustments}
              onUpdateImageAdjustments={updateImageAdjustments}
              textElements={textElements}
              onAddTextElement={addTextElement}
              onUpdateTextElement={updateTextElement}
              onRemoveTextElement={removeTextElement}
              shapeElements={shapeElements}
              onAddShapeElement={addShapeElement}
              onUpdateShapeElement={updateShapeElement}
              onRemoveShapeElement={removeShapeElement}
              assetElements={assetElements}
              onAddAssetElement={addAssetElement}
              onUpdateAssetElement={updateAssetElement}
              onRemoveAssetElement={removeAssetElement}
              selectedElement={selectedElement}
              projectId={projectId}
              userId={user?.id} // Pass userId from auth context
              initialTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ThumbnailEditor;